import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Calendar, 
  Clock, 
  User, 
  Mail, 
  Euro,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  ArrowLeft,
  CreditCard,
  Info,
  Ban,
  Loader2,
  MapPin
} from 'lucide-react';
import axios from 'axios';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingManagePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const registrationId = searchParams.get('id');
  const email = searchParams.get('email');
  const actionParam = searchParams.get('action');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [booking, setBooking] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [canCancel, setCanCancel] = useState(false);
  const [canReschedule, setCanReschedule] = useState(false);
  
  const [activeTab, setActiveTab] = useState(actionParam === 'cancel' ? 'cancel' : 'reschedule');
  const [selectedNewDate, setSelectedNewDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [newDateDisplay, setNewDateDisplay] = useState('');

  useEffect(() => {
    const fetchBookingDetails = async () => {
      if (!registrationId || !email) {
        setError('Ungültiger Link. Bitte verwenden Sie den Link aus Ihrer Bestätigungs-E-Mail.');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${API_URL}/api/booking/${registrationId}?email=${encodeURIComponent(email)}`
        );
        setBooking(response.data.registration);
        setAvailableDates(response.data.available_dates || []);
        setCanCancel(response.data.can_cancel);
        setCanReschedule(response.data.can_reschedule);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Buchung nicht gefunden. Bitte überprüfen Sie den Link.');
        } else if (err.response?.status === 403) {
          setError('Die E-Mail-Adresse stimmt nicht mit der Buchung überein.');
        } else if (err.response?.status === 400) {
          setError(err.response.data?.detail || 'Diese Buchung wurde bereits bearbeitet.');
        } else {
          setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [registrationId, email]);

  const handleCancel = async () => {
    if (!window.confirm('Möchten Sie Ihre Buchung wirklich stornieren? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/booking/cancel`, {
        registration_id: registrationId,
        email: email
      });
      setSuccess('cancel');
      toast.success('Buchung erfolgreich storniert');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Stornieren der Buchung.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReschedule = async () => {
    if (!selectedNewDate) {
      toast.error('Bitte wählen Sie einen neuen Termin aus.');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/booking/reschedule`, {
        registration_id: registrationId,
        email: email,
        new_date_id: selectedNewDate.id
      });
      setNewDateDisplay(response.data.new_date || selectedNewDate.display_date);
      setSuccess('reschedule');
      toast.success('Erfolgreich umgebucht!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler bei der Umbuchung.');
    } finally {
      setSubmitting(false);
    }
  };

  // Get payment method label
  const getPaymentMethodLabel = (method) => {
    const labels = {
      'paypal': 'PayPal',
      'transfer': 'Überweisung',
      'cash': 'Barzahlung vor Ort',
      'bg': 'BG-Kostenübernahme (kostenfrei)'
    };
    return labels[method] || method;
  };

  // Success Screen
  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Navbar />
        <main className="flex-1 py-12 px-4">
          <div className="max-w-lg mx-auto">
            <Card className="text-center">
              <CardContent className="pt-8 pb-8">
                {success === 'cancel' && (
                  <>
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <XCircle className="w-8 h-8 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-3">Stornierung bestätigt</h2>
                    <p className="text-slate-600 mb-4">
                      Ihre Buchung wurde erfolgreich storniert. Der Platz wurde wieder freigegeben.
                    </p>
                    <p className="text-sm text-slate-500 mb-6">
                      Sie erhalten eine Bestätigung per E-Mail an <strong>{email}</strong>.
                    </p>
                  </>
                )}
                {success === 'reschedule' && (
                  <>
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-3">Umbuchung bestätigt</h2>
                    <p className="text-slate-600 mb-2">
                      Ihre Buchung wurde erfolgreich umgebucht.
                    </p>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <p className="text-green-800 font-semibold">
                        Neuer Termin: {newDateDisplay}
                      </p>
                    </div>
                    <p className="text-sm text-slate-500 mb-6">
                      Sie erhalten eine Bestätigung per E-Mail an <strong>{email}</strong>.
                    </p>
                  </>
                )}
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button 
                    onClick={() => navigate('/')}
                    className="bg-[#1e3a5f] hover:bg-[#2d4a6f]"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Zur Startseite
                  </Button>
                  {success === 'cancel' && (
                    <Button 
                      onClick={() => navigate('/termine')}
                      variant="outline"
                    >
                      Neuen Termin buchen
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-[#1e3a5f] mx-auto mb-4" />
            <p className="text-slate-600">Buchung wird geladen...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Navbar />
        <main className="flex-1 py-12 px-4">
          <div className="max-w-lg mx-auto">
            <Card className="text-center">
              <CardContent className="pt-8 pb-8">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-800 mb-3">Fehler</h2>
                <p className="text-slate-600 mb-6">{error}</p>
                <Button 
                  onClick={() => navigate('/')}
                  variant="outline"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zur Startseite
                </Button>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // PayPal - No Changes Allowed
  if (!canCancel && !canReschedule) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <Navbar />
        <main className="flex-1 py-12 px-4">
          <div className="max-w-lg mx-auto">
            <Card className="text-center">
              <CardContent className="pt-8 pb-8">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Ban className="w-8 h-8 text-amber-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-800 mb-4">Änderung nicht möglich</h2>
                
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4 text-left">
                  <p className="text-sm text-slate-600 mb-2"><strong>Ihre Buchung:</strong></p>
                  <p className="text-slate-800">{booking?.first_name} {booking?.last_name}</p>
                  <p className="text-slate-800">{booking?.course_date}</p>
                  <p className="text-slate-600 text-sm">Zahlungsart: {getPaymentMethodLabel(booking?.payment_method)}</p>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <p className="text-amber-800 text-sm">
                    <strong>Hinweis:</strong> Bei Zahlung per PayPal ist eine Stornierung oder Umbuchung ausgeschlossen.
                  </p>
                </div>
                
                <p className="text-sm text-slate-500 mb-6">
                  Bei dringenden Anliegen kontaktieren Sie uns bitte direkt:<br />
                  📞 0176 / 41673000 · ✉️ info@erstehilfebottrop.de
                </p>
                
                <Button 
                  onClick={() => navigate('/')}
                  className="bg-[#1e3a5f] hover:bg-[#2d4a6f]"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zur Startseite
                </Button>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />
      
      <main className="flex-1 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f] text-center mb-6">
            Buchung verwalten
          </h1>

          {/* Current Booking Info */}
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-[#1e3a5f]">Ihre aktuelle Buchung</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Name</p>
                    <p className="font-medium">{booking?.first_name} {booking?.last_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">E-Mail</p>
                    <p className="font-medium text-sm">{booking?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Termin</p>
                    <p className="font-medium">{booking?.course_date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Uhrzeit</p>
                    <p className="font-medium">{booking?.course_time}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Euro className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Paket / Betrag</p>
                    <p className="font-medium">
                      {booking?.package_name || 'Erste Hilfe Kurs'} 
                      {booking?.amount > 0 && ` – ${booking?.amount?.toFixed(2)} €`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <CreditCard className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Zahlungsart</p>
                    <p className="font-medium">{getPaymentMethodLabel(booking?.payment_method)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-blue-800">
                  {booking?.payment_method === 'bg' 
                    ? 'Bei BG-Kursen können Sie kostenfrei stornieren oder umbuchen.'
                    : 'Bei Barzahlung können Sie kostenfrei stornieren oder umbuchen.'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Action Tabs */}
          <div className="flex gap-2 mb-4">
            {canReschedule && (
              <Button
                variant={activeTab === 'reschedule' ? 'default' : 'outline'}
                onClick={() => setActiveTab('reschedule')}
                className={activeTab === 'reschedule' ? 'bg-amber-500 hover:bg-amber-600' : ''}
                data-testid="tab-reschedule"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Umbuchen
              </Button>
            )}
            {canCancel && (
              <Button
                variant={activeTab === 'cancel' ? 'default' : 'outline'}
                onClick={() => setActiveTab('cancel')}
                className={activeTab === 'cancel' ? 'bg-red-500 hover:bg-red-600' : ''}
                data-testid="tab-cancel"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Stornieren
              </Button>
            )}
          </div>

          {/* Reschedule Panel */}
          {activeTab === 'reschedule' && canReschedule && (
            <Card className="border-amber-200">
              <CardHeader>
                <CardTitle className="text-lg text-amber-600">Auf anderen Termin umbuchen</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-4">
                  Wählen Sie einen neuen Termin aus. Die Umbuchung erfolgt sofort automatisch.
                </p>
                
                {availableDates.length > 0 ? (
                  <div className="space-y-2 max-h-72 overflow-y-auto mb-4">
                    {availableDates.map((date) => (
                      <div
                        key={date.id}
                        onClick={() => setSelectedNewDate(date)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          selectedNewDate?.id === date.id
                            ? 'border-green-500 bg-green-50'
                            : 'border-slate-200 hover:border-amber-300'
                        }`}
                        data-testid={`date-option-${date.id}`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium text-slate-800">{date.display_date}</p>
                            <p className="text-sm text-slate-500">{date.time}</p>
                            <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" /> {date.location}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              date.spots_left > 5 
                                ? 'bg-green-100 text-green-700' 
                                : date.spots_left > 2
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-red-100 text-red-700'
                            }`}>
                              {date.spots_left} Plätze frei
                            </span>
                            {selectedNewDate?.id === date.id && (
                              <CheckCircle className="w-5 h-5 text-green-600 mt-2 ml-auto" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4 bg-slate-50 rounded-lg">
                    Aktuell sind keine weiteren Termine verfügbar.
                  </p>
                )}

                {availableDates.length > 0 && (
                  <Button
                    onClick={handleReschedule}
                    disabled={!selectedNewDate || submitting}
                    className="w-full bg-amber-500 hover:bg-amber-600 h-12"
                    data-testid="confirm-reschedule-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Wird umgebucht...
                      </>
                    ) : (
                      'Jetzt umbuchen'
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Cancel Panel */}
          {activeTab === 'cancel' && canCancel && (
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-lg text-red-600">Termin stornieren</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-800">Achtung</p>
                      <p className="text-sm text-red-700">
                        Wenn Sie Ihre Buchung stornieren, wird der Platz wieder freigegeben. 
                        Diese Aktion kann nicht rückgängig gemacht werden.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-slate-700">
                    <strong>Zu stornieren:</strong><br />
                    {booking?.course_date} – {booking?.course_time}
                  </p>
                </div>

                <Button
                  onClick={handleCancel}
                  disabled={submitting}
                  variant="destructive"
                  className="w-full h-12"
                  data-testid="confirm-cancel-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Wird storniert...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 mr-2" />
                      Buchung endgültig stornieren
                    </>
                  )}
                </Button>

                {canReschedule && (
                  <p className="text-sm text-slate-500 text-center mt-4">
                    Oder möchten Sie lieber{' '}
                    <button 
                      onClick={() => setActiveTab('reschedule')}
                      className="text-amber-600 font-medium underline"
                    >
                      auf einen anderen Termin umbuchen
                    </button>?
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Back Link */}
          <div className="text-center mt-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-slate-500"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück zur Startseite
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BookingManagePage;
